import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { vorgangService, type VorgangPickerTodo } from "../../services/vorgangService";
import { vorgangNoteService, type AggregatedNoteDto } from "../../services/vorgangNoteService";
import { documentService, type DocumentDto } from "../../services/documentService";
import { ausgangText, wegeFuer } from "./shared/zusammenfuehrWege";
import { IcoX } from "./v3/vg3Icons";
import { DateiTypPlakette } from "../documents/DateiTypSymbol";
import "./v3/vorgangV3.css";
import "./v4/vorgangV4.css";

/**
 * VORGÄNGE ZUSAMMENFÜHREN — DIE GEGENÜBERSTELLUNG (PO 22.09.2026).
 *
 * > „das ist zu verwirrend, vorallem die optionen, das wird auf anhieb keiner
 * >  verstehen. Wir stellen die vorgänge nebeneinander, und nicht die notizen
 * >  und die details anklickbar zum auswählen sondern die todos und deren
 * >  anhänge."
 *
 * ══ WAS SICH GEÄNDERT HAT ═════════════════════════════════════════════════
 * Vorher stand oben eine Frage („Ist es dasselbe zweimal?"), die der Anwender
 * beantworten musste, bevor er überhaupt sah, worum es geht. Jetzt steht links
 * der Sieger und rechts, was drüben liegt — und die EBENE, auf der man anhakt,
 * ist die Entscheidung. Keine Frage vorweg, kein Schalter.
 *
 * ══ DIE EINHEIT IST DAS TO-DO, NICHT DIE NOTIZ ════════════════════════════
 * Das ist der zweite Teil des Auftrags und der Grund, warum die alte Liste
 * falsch war: Sie zeigte Notizen und Dokumente, also die kleinste Einheit.
 * Gearbeitet wird aber in To-dos. Wer ein To-do anhakt, nimmt seinen ganzen
 * Schriftwechsel mit — darüber muss er nicht einzeln entscheiden.
 *
 * Einzeln abwählbar sind Notizen und Dokumente nur dort, wo es KEINE To-dos
 * gibt: bei zwei schlanken Vorgängen, dem klassischen Duplikat.
 *
 * ══ WELCHER WEG WANN GILT ═════════════════════════════════════════════════
 * Steht in `shared/zusammenfuehrWege.ts`, mitsamt der Begründung, warum „als
 * To-do übernehmen" ein ZWANG ist, sobald der Sieger To-dos hat — und nicht
 * bloß ein Vorschlag.
 */

export interface ZusammenfuehrenKandidat {
    id: number;
    titel: string;
    vorgangNbr?: string | null;
    /** Für die Vorauswahl „der Älteste bleibt". */
    createdAt?: string | null;
}

type Inhalt = {
    todos: VorgangPickerTodo[];
    notizen: AggregatedNoteDto[];
    dokumente: DocumentDto[];
};

/** Der Älteste — er trägt die Nummer, die schon im Umlauf ist. */
export function vorgeschlagenesZiel(kandidaten: ZusammenfuehrenKandidat[]): number | null {
    if (!kandidaten.length) return null;
    const sortiert = [...kandidaten].sort((a, b) => {
        const av = a.createdAt ? Date.parse(a.createdAt) : Number.POSITIVE_INFINITY;
        const bv = b.createdAt ? Date.parse(b.createdAt) : Number.POSITIVE_INFINITY;
        if (av !== bv) return av - bv;
        return a.id - b.id;
    });
    return sortiert[0]!.id;
}

/** Kurzfassung einer Notiz für die Liste — eine Zeile, nicht der ganze Block. */
export function notizVorschau(text: string | null | undefined, max = 70): string {
    const roh = (text ?? "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
    if (!roh) return "(leere Notiz)";
    return roh.length > max ? roh.slice(0, max - 1) + "…" : roh;
}

/**
 * Was NICHT mitwandern soll — nur beim einzelnen Abwählen (zwei schlanke
 * Vorgänge). In jedem anderen Weg bleibt nichts zurück, was der Anwender
 * abgewählt hätte; eine übriggebliebene Abwahl von vorher darf dann NICHT
 * mitgeschickt werden. Genau das ist die Falle: Sie wäre unsichtbar, käme aber
 * beim Server an.
 */
export function abwahlNutzlast(
    einzelnWaehlbar: boolean,
    inhalt: Inhalt | undefined,
    abgewaehlt: ReadonlySet<string>,
): { ohneNotizen: number[]; ohneDokumente: number[] } {
    if (!einzelnWaehlbar) return { ohneNotizen: [], ohneDokumente: [] };
    return {
        ohneNotizen: (inhalt?.notizen ?? [])
            .filter((n) => n.source === "vorgang" && abgewaehlt.has(`notiz:${n.id}`))
            .map((n) => n.id),
        ohneDokumente: (inhalt?.dokumente ?? [])
            .filter((d) => abgewaehlt.has(`dok:${d.id}`))
            .map((d) => d.id),
    };
}

const leererInhalt: Inhalt = { todos: [], notizen: [], dokumente: [] };

export const VorgaengeZusammenfuehrenDialog: React.FC<{
    kandidaten: ZusammenfuehrenKandidat[];
    onClose: () => void;
    onFertig: (zielId: number, verschwundeneIds: number[]) => void;
}> = ({ kandidaten, onClose, onFertig }) => {
    const [zielId, setZielId] = useState<number | null>(() => vorgeschlagenesZiel(kandidaten));
    const [inhalte, setInhalte] = useState<Map<number, Inhalt>>(new Map());
    const [laden, setLaden] = useState(true);
    const [busy, setBusy] = useState(false);
    const [fehler, setFehler] = useState<string | null>(null);
    /** Abgewählt — Notizen/Dokumente, die NICHT mitwandern. */
    const [abgewaehlt, setAbgewaehlt] = useState<Set<string>>(new Set());
    /** Angehakte To-dos je Quellvorgang — `vorgangId:todoId`. */
    const [gewaehlteTodos, setGewaehlteTodos] = useState<Set<string>>(new Set());
    /** Quellvorgänge, die als Ganzes zu einem To-do werden sollen. */
    const [alsTodo, setAlsTodo] = useState<Set<number>>(new Set());

    const quellen = useMemo(() => kandidaten.filter((k) => k.id !== zielId), [kandidaten, zielId]);
    const zielInhalt = inhalte.get(zielId ?? -1) ?? leererInhalt;
    const zielForm = { hatTodos: zielInhalt.todos.length > 0 };

    useEffect(() => {
        let abgebrochen = false;
        void (async () => {
            setLaden(true);
            try {
                const paare = await Promise.all(
                    kandidaten.map(async (k) => {
                        const [todos, notizen, dokumente] = await Promise.all([
                            vorgangService.pickerTodos(k.id).catch(() => []),
                            vorgangNoteService.listAggregated(k.id).catch(() => []),
                            documentService.list({ vorgangId: k.id }).catch(() => []),
                        ]);
                        return [k.id, { todos, notizen, dokumente }] as const;
                    }),
                );
                if (!abgebrochen) setInhalte(new Map(paare));
            } finally {
                if (!abgebrochen) setLaden(false);
            }
        })();
        return () => {
            abgebrochen = true;
        };
    }, [kandidaten]);

    /**
     * Wechselt der Sieger, ändert sich womöglich der erlaubte Weg — eine
     * Auswahl von vorher wäre dann eine, die es nicht mehr gibt. Sie
     * stehenzulassen hieße, unsichtbar etwas mitzuschicken.
     */
    useEffect(() => {
        setAbgewaehlt(new Set());
        setGewaehlteTodos(new Set());
        setAlsTodo(new Set());
    }, [zielId]);

    const umschalten = useCallback((setter: typeof setAbgewaehlt, schluessel: string) => {
        setter((alt) => {
            const neu = new Set(alt);
            if (neu.has(schluessel)) neu.delete(schluessel);
            else neu.add(schluessel);
            return neu;
        });
    }, []);

    /** Beim Zwang „als To-do" ist die Wahl schon getroffen — sie wird gesetzt. */
    useEffect(() => {
        if (laden || zielId == null) return;
        const erzwungene = quellen
            .filter((q) => wegeFuer(zielForm, formVon(q.id)).alsTodoErzwungen)
            .map((q) => q.id);
        if (!erzwungene.length) return;
        setAlsTodo((alt) => {
            const neu = new Set(alt);
            for (const id of erzwungene) neu.add(id);
            return neu;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [laden, zielId, quellen.length, inhalte]);

    function formVon(id: number) {
        return { hatTodos: (inhalte.get(id)?.todos.length ?? 0) > 0 };
    }

    const fuehreZusammen = async () => {
        if (zielId == null || busy) return;
        setBusy(true);
        setFehler(null);
        const verschwunden: number[] = [];
        try {
            // NACHEINANDER: Jeder Lauf kann den Strang des Ziels umbauen. Zwei
            // gleichzeitige sähen denselben Zwischenstand und zögen beide ein
            // Übernahme-To-do hoch.
            for (const q of quellen) {
                const inhalt = inhalte.get(q.id);
                const wege = wegeFuer(zielForm, formVon(q.id));

                if (wege.todosWaehlbar) {
                    const ids = (inhalt?.todos ?? [])
                        .filter((t) => gewaehlteTodos.has(`${q.id}:${t.id}`))
                        .map((t) => t.id);
                    if (!ids.length) continue;
                    const e = await vorgangService.uebernimmTodosAusVorgang(zielId, q.id, ids);
                    if (e.quelleGeloescht) verschwunden.push(q.id);
                } else if (alsTodo.has(q.id)) {
                    await vorgangService.verschiebeVorgangAlsTodo(q.id, zielId);
                    verschwunden.push(q.id);
                } else if (wege.einzelnWaehlbar) {
                    await vorgangService.verschmelzeVorgaenge(
                        zielId,
                        q.id,
                        abwahlNutzlast(true, inhalt, abgewaehlt),
                    );
                    verschwunden.push(q.id);
                }
            }
            onFertig(zielId, verschwunden);
            onClose();
        } catch (err: any) {
            console.error("[VorgaengeZusammenfuehren] fehlgeschlagen", err);
            setFehler(err?.message ?? "Zusammenführen fehlgeschlagen.");
        } finally {
            setBusy(false);
        }
    };

    /** Hat der Anwender überhaupt etwas ausgewählt? */
    const etwasGewaehlt = quellen.some((q) => {
        const wege = wegeFuer(zielForm, formVon(q.id));
        if (wege.todosWaehlbar) {
            return (inhalte.get(q.id)?.todos ?? []).some((t) =>
                gewaehlteTodos.has(`${q.id}:${t.id}`),
            );
        }
        return alsTodo.has(q.id) || wege.einzelnWaehlbar;
    });

    const beschriftung = (k: ZusammenfuehrenKandidat) =>
        `${k.vorgangNbr ? `${k.vorgangNbr} · ` : ""}${k.titel || "(ohne Betreff)"}`;

    return createPortal(
        <div className="vg4-schleier" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
            <div
                className="kvp-rd vg4-dialog vgdup"
                role="dialog"
                aria-modal="true"
                aria-label="Vorgänge zusammenführen"
            >
                <div className="vg3-modal-kopf">
                    <span className="vg3-eyebrow">Vorgänge zusammenführen</span>
                    <button type="button" className="vg3-ibtn" title="Schließen" aria-label="Schließen" onClick={onClose}>
                        <IcoX size={16} />
                    </button>
                </div>

                {fehler && (
                    <div className="vg3-fehler" role="alert">
                        {fehler}
                    </div>
                )}

                {/* WER BLEIBT — OBEN UND FEST (T-246, 12.10.2026: „ich muss
                    auswählen können, welcher Vorgang führend ist").
                    Die Wahl stand bis dahin ganz unten IM scrollenden Inhalt
                    (`.vgdup-b`, max. 560 px) — „weil es die seltenere Änderung
                    ist". Schon ein paar To-dos auf beiden Seiten schoben sie
                    aus dem sichtbaren Bereich; wer sie dann doch fand, verlor
                    beim Wechsel seine Häkchen (der Wechsel setzt sie bewusst
                    zurück, s. o.). Jetzt steht sie vor allem anderen, außerhalb
                    des Scrollbereichs — man trifft sie VOR dem Anhaken. */}
                <div className="vgdup-ziele-kopf">
                    <span className="vgdup-quelle-h">Welcher Vorgang bleibt (führend)?</span>
                    <div className="vgdup-ziele" role="radiogroup" aria-label="Welcher Vorgang bleibt?">
                        {kandidaten.map((k) => (
                            <label key={k.id} className={"vgdup-ziel" + (k.id === zielId ? " an" : "")}>
                                <input
                                    type="radio"
                                    name="vg-zf-ziel"
                                    checked={k.id === zielId}
                                    disabled={busy}
                                    onChange={() => setZielId(k.id)}
                                />
                                <span className="vgdup-ziel-t">{beschriftung(k)}</span>
                            </label>
                        ))}
                    </div>
                </div>

                <div className="vgdup-b">
                    {laden ? (
                        <p className="vg3-modal-hinweis">Vorgänge werden geladen…</p>
                    ) : (
                        quellen.map((q) => {
                            const wege = wegeFuer(zielForm, formVon(q.id));
                            const quelleInhalt = inhalte.get(q.id) ?? leererInhalt;
                            const gewaehlt = quelleInhalt.todos.filter((t) =>
                                gewaehlteTodos.has(`${q.id}:${t.id}`),
                            ).length;

                            return (
                                <div key={q.id} className="vgdup-paar">
                                    {/* LINKS: der Sieger — reine Anzeige. */}
                                    <div className="vgdup-seite">
                                        <div className="vgdup-seite-k">
                                            <span className="vgdup-marke">Bleibt</span>
                                            {beschriftung(kandidaten.find((k) => k.id === zielId)!)}
                                        </div>
                                        {zielInhalt.todos.length > 0 ? (
                                            <ul className="vgdup-liste stumm">
                                                {zielInhalt.todos.map((t) => (
                                                    <li key={t.id} className="vgdup-zeile">
                                                        <span className="vgdup-art">To-do</span>
                                                        <span className="vgdup-text">{t.titel}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <p className="vg3-modal-hinweis">
                                                Trägt seinen Schriftwechsel selbst — noch keine To-dos.
                                            </p>
                                        )}
                                    </div>

                                    {/* RECHTS: was drüben liegt — hier wird gewählt. */}
                                    <div className="vgdup-seite">
                                        <div className="vgdup-seite-k">
                                            <span className="vgdup-marke weg">Wandert</span>
                                            {beschriftung(q)}
                                            {/* T-246: direkt am Paar umkehren — dieser bleibt. */}
                                            <button
                                                type="button"
                                                className="vgdup-tausch"
                                                disabled={busy}
                                                title={`Stattdessen bleibt „${q.titel || "(ohne Betreff)"}“`}
                                                onClick={() => setZielId(q.id)}
                                            >
                                                ⇄ Soll bleiben
                                            </button>
                                        </div>

                                        {wege.todosWaehlbar && (
                                            <div className="vgdup-liste">
                                                {quelleInhalt.todos.map((t) => (
                                                    <label key={t.id} className="vgdup-zeile">
                                                        <input
                                                            type="checkbox"
                                                            checked={gewaehlteTodos.has(`${q.id}:${t.id}`)}
                                                            disabled={busy}
                                                            onChange={() =>
                                                                umschalten(setGewaehlteTodos, `${q.id}:${t.id}`)
                                                            }
                                                        />
                                                        <span className="vgdup-art">To-do</span>
                                                        <span className="vgdup-text">{t.titel}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        )}

                                        {wege.alsTodoMoeglich && (
                                            <label
                                                className={
                                                    "vgdup-zeile ganz" + (wege.alsTodoErzwungen ? " fest" : "")
                                                }
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={alsTodo.has(q.id)}
                                                    disabled={busy || wege.alsTodoErzwungen}
                                                    onChange={() =>
                                                        setAlsTodo((alt) => {
                                                            const neu = new Set(alt);
                                                            if (neu.has(q.id)) neu.delete(q.id);
                                                            else neu.add(q.id);
                                                            return neu;
                                                        })
                                                    }
                                                />
                                                <span className="vgdup-text">
                                                    Als To-do übernehmen — der Betreff bleibt die Überschrift
                                                    {wege.alsTodoErzwungen && (
                                                        <small>
                                                            {" "}
                                                            Der Zielvorgang ist in To-dos gegliedert; anders
                                                            ginge der Betreff verloren.
                                                        </small>
                                                    )}
                                                </span>
                                            </label>
                                        )}

                                        {wege.einzelnWaehlbar && !alsTodo.has(q.id) && (
                                            <div className="vgdup-liste">
                                                {quelleInhalt.notizen
                                                    .filter((n) => n.source === "vorgang")
                                                    .map((n) => (
                                                        <label key={`notiz:${n.id}`} className="vgdup-zeile">
                                                            <input
                                                                type="checkbox"
                                                                checked={!abgewaehlt.has(`notiz:${n.id}`)}
                                                                disabled={busy}
                                                                onChange={() =>
                                                                    umschalten(setAbgewaehlt, `notiz:${n.id}`)
                                                                }
                                                            />
                                                            <span className="vgdup-art">Notiz</span>
                                                            <span className="vgdup-text">
                                                                {notizVorschau(n.text)}
                                                            </span>
                                                        </label>
                                                    ))}
                                                {quelleInhalt.dokumente.map((d) => (
                                                    <label key={`dok:${d.id}`} className="vgdup-zeile">
                                                        <input
                                                            type="checkbox"
                                                            checked={!abgewaehlt.has(`dok:${d.id}`)}
                                                            disabled={busy}
                                                            onChange={() => umschalten(setAbgewaehlt, `dok:${d.id}`)}
                                                        />
                                                        <span className="vgdup-art">Dokument</span>
                                                        <DateiTypPlakette fileName={d.fileName} mimeType={d.mimeType} />
                                                        <span className="vgdup-text">{d.fileName}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        )}

                                        {/* Der Ausgang steht VOR der Handlung da. */}
                                        <p className="vgdup-ausgang">
                                            {ausgangText({
                                                todosGesamt: quelleInhalt.todos.length,
                                                todosGewaehlt: gewaehlt,
                                                alsTodo: alsTodo.has(q.id),
                                                titel: q.titel,
                                            })}
                                        </p>
                                    </div>
                                </div>
                            );
                        })
                    )}

                </div>

                <div className="vg3-modal-fuss">
                    <button type="button" className="vg3-btn" disabled={busy} onClick={onClose}>
                        Abbrechen
                    </button>
                    <button
                        type="button"
                        className="vg3-btn primary"
                        disabled={busy || laden || zielId == null || !quellen.length || !etwasGewaehlt}
                        onClick={() => void fuehreZusammen()}
                    >
                        {busy ? "Wird zusammengeführt…" : "Zusammenführen"}
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default VorgaengeZusammenfuehrenDialog;
